import { Phone, MessageCircle, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent, getWhatsAppUrl } from "@/lib/tracking";

interface CTABlockProps {
  headline?: string;
  subtext?: string;
  service?: string;
}

export function CTABlock({
  headline = "Ready to Get Started?",
  subtext = "Give us a call, message on WhatsApp, or request a booking online. No pressure — just honest advice.",
  service,
}: CTABlockProps) {
  const biz = getBusinessConfig();
  const waUrl = getWhatsAppUrl(service);

  return (
    <section className="relative surface-dark grain-overlay overflow-hidden">
      <div className="relative section-y mx-auto max-w-4xl px-4 md:px-6 text-center">
        <p className="eyebrow text-primary/90">Talk to a mechanic</p>
        <h2 className="mt-4 display-lg text-white">{headline}</h2>
        <p className="mt-5 text-base md:text-lg text-white/85 leading-relaxed max-w-2xl mx-auto">
          {subtext}
        </p>
        <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2.5">
          <a
            href={biz.phoneHref}
            onClick={() => trackEvent("call_click", { location: "cta_block" })}
            className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground hover:bg-primary/90 transition-all shadow-xl shadow-primary/30 ring-1 ring-primary/40"
          >
            <Phone className="h-5 w-5" />
            Call {biz.phone}
          </a>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", { location: "cta_block" })}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-6 py-4 text-base font-bold text-white hover:bg-[#1ea952] transition-colors shadow-lg shadow-[#25D366]/20"
          >
            <MessageCircle className="h-5 w-5" />
            WhatsApp
          </a>
          <Link
            to="/book"
            onClick={() => trackEvent("service_cta_click", { location: "cta_block" })}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/25 bg-white/5 backdrop-blur-sm px-6 py-4 text-base font-bold text-white hover:bg-white/10 transition-colors"
          >
            Request a Booking
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs text-white/90">
          <span>Call is fastest</span>
          <span className="text-white/80" aria-hidden="true">·</span>
          <span>No work without your approval</span>
          <span className="text-white/80" aria-hidden="true">·</span>
          <span>Replies during workshop hours · Mon–Sat 9am–6pm</span>
        </div>
      </div>
    </section>
  );
}
